import React, { useState, useRef, useEffect } from "react";
import { FieldRenderProps, FieldWrapper } from "@progress/kendo-react-form";
import { Label, Error, Hint } from "@progress/kendo-react-labels";
import { ErrorToast } from "../toast/Toasts";
import { RiImageAddFill } from "react-icons/ri";
import IconButton from "../common/IconButton";

const FormImageUpload = (fieldRenderProps: FieldRenderProps) => {
  const {
    valid,
    value,
    id,
    optional,
    label,
    hint,
    astrike = false,
    validationMessage,
    wrapperStyle,
    alternateName = "Image",
    maxSize = 10,
    acceptedFormats = "PNG, JPG, GIF",
    touched,
    onChange,
    multiple = false,
    ...others
  } = fieldRenderProps;

  const [isDragging, setIsDragging] = useState(false);
  const [previews, setPreviews] = useState<
    Array<{ file: File | string; preview: string }>
  >([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const showValidationMessage = touched && validationMessage;
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${id}_hint` : "";
  const errorId = showValidationMessage ? `${id}_error` : "";
  const labelId = label ? `${id}_label` : "";

  useEffect(() => {
    if (value) {
      if (Array.isArray(value)) {
        // Handle multiple files
        const newPreviews: Array<{ file: File | string; preview: string }> = [];

        value.forEach((item) => {
          if (item instanceof File) {
            const reader = new FileReader();
            reader.onload = (e) => {
              newPreviews.push({
                file: item,
                preview: e.target?.result as string,
              });
              if (newPreviews.length === value.length) {
                setPreviews(newPreviews);
              }
            };
            reader.readAsDataURL(item);
          } else if (typeof item === "string") {
            newPreviews.push({
              file: item,
              preview: item,
            });
          }
        });

        if (newPreviews.length === value.length) {
          setPreviews(newPreviews);
        }
      } else if (value instanceof File) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setPreviews([
            {
              file: value,
              preview: e.target?.result as string,
            },
          ]);
        };
        reader.readAsDataURL(value);
      } else if (typeof value === "string") {
        setPreviews([
          {
            file: value,
            preview: value,
          },
        ]);
      }
    } else {
      setPreviews([]);
    }
  }, [value]);

  const handleFileChange = (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    const validFiles: File[] = [];

    for (const file of fileArray) {
      // Validate file size
      if (file.size > maxSize * 1024 * 1024) {
        ErrorToast(
          `${file.name} is too large. File size must be under ${maxSize}MB.`,
        );
        continue;
      }

      // Validate file type
      const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/gif"];
      if (!validTypes.includes(file.type)) {
        ErrorToast(
          `${file.name} is not a valid image file (${acceptedFormats}).`,
        );
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    if (multiple) {
      // Append to existing files
      const currentFiles = Array.isArray(value) ? value : value ? [value] : [];
      onChange({ value: [...currentFiles, ...validFiles] });
    } else {
      // Replace with single file
      onChange({ value: validFiles[0] });
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileChange(files);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileChange(files);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemove = (indexToRemove: number) => {
    if (multiple && Array.isArray(value)) {
      const newFiles = value.filter((_, index) => index !== indexToRemove);
      onChange({ value: newFiles.length > 0 ? newFiles : null });
    } else {
      onChange({ value: null });
      setPreviews([]);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <FieldWrapper style={{ width: "100%", ...wrapperStyle }}>
      <div
        className="k-form-field-wrap"
        style={{ width: "100%", height: "100%" }}
      >
        {label && (
          <Label
            id={labelId}
            editorId={id}
            editorValid={valid}
            optional={optional}
            style={{ marginBottom: "8px", display: "block" }}
          >
            {label} {astrike && <span style={{ color: "red" }}>*</span>}
          </Label>
        )}

        <div
          onClick={handleClick}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          style={{
            width: "100%",
            minHeight: "160px",
            height: "87%",
            border: isDragging
              ? "2px solid #0078d4"
              : showValidationMessage
                ? "2px dashed #dc3545"
                : "2px dashed #d1d5db",
            borderRadius: "8px",
            backgroundColor: isDragging
              ? "#f0f8ff"
              : previews.length > 0
                ? "#f9fafb"
                : "#ffffff",
            cursor: "pointer",
            transition: "all 0.2s ease",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            position: "relative",
          }}
          aria-describedby={`${hintId} ${errorId}`}
          aria-labelledby={labelId}
        >
          {previews.length > 0 ? (
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              <div
                style={{
                  width: "100%",
                  overflowX: "auto",
                  overflowY: "hidden",
                  display: "flex",
                  gap: "12px",
                  padding: "8px 0",
                  scrollbarWidth: "thin",
                  scrollbarColor: "#cbd5e0 #f3f4f6",
                  justifyContent: "center",
                }}
              >
                {previews.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      position: "relative",
                      flexShrink: 0,
                      width: "120px",
                      height: "120px",
                      borderRadius: "8px",
                      overflow: "hidden",
                      border: "1px solid #e5e7eb",
                      backgroundColor: "#ffffff",
                    }}
                  >
                    <img
                      src={item.preview}
                      alt={`${alternateName} ${index + 1}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (typeof item.file === "string") {
                          window.open(item.file, "_blank");
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemove(index);
                      }}
                      style={{
                        position: "absolute",
                        top: "4px",
                        right: "4px",
                        width: "24px",
                        height: "24px",
                        backgroundColor: "rgba(220, 53, 69, 0.9)",
                        color: "white",
                        border: "none",
                        borderRadius: "50%",
                        fontSize: "15px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                        padding: "0",
                      }}
                      title="Remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              {multiple && (
                <div style={{ textAlign: "center" }}>
                  <span
                    style={{
                      color: "#0078d4",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    Click to add more images
                  </span>
                </div>
              )}
            </div>
          ) : (
            <>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  backgroundColor: "#f3f4f6",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "12px",
                }}
              >
                <IconButton
                  type="button"
                  fillMode="flat"
                  size="small"
                  icon={RiImageAddFill}
                  iconStyle={{
                    width: "24px",
                    height: "24px",
                    color: "#6b7280",
                  }}
                />
              </div>
              <div style={{ textAlign: "center" }}>
                <span
                  style={{
                    color: "#0078d4",
                    fontWeight: "500",
                    fontSize: "14px",
                  }}
                >
                  Click to upload
                </span>
                <span
                  style={{
                    color: "#6b7280",
                    fontSize: "14px",
                    marginLeft: "4px",
                  }}
                >
                  or drag and drop
                </span>
              </div>
              <p
                style={{
                  color: "#9ca3af",
                  fontSize: "12px",
                  marginTop: "4px",
                  marginBottom: "0",
                }}
              >
                {acceptedFormats} up to {maxSize}MB
              </p>
            </>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          id={id}
          accept="image/png,image/jpeg,image/jpg,image/gif"
          onChange={handleInputChange}
          multiple={multiple}
          style={{ display: "none" }}
          {...others}
        />

        {showHint && (
          <Hint id={hintId} style={{ marginTop: "4px" }}>
            {hint}
          </Hint>
        )}
        {showValidationMessage && (
          <Error id={errorId} style={{ marginTop: "4px" }}>
            {validationMessage}
          </Error>
        )}
      </div>
    </FieldWrapper>
  );
};

export default FormImageUpload;
